/**
 * The order panel on the right of the till - the bill as it is being built.
 *
 * Follows the layout: a navy header with the order number, the Item / Qty /
 * Rate / Total table, the totals block, the Discount / Note / Hold / Clear row,
 * the Cash / Card / Wallet strip, and the wide Pay button.
 *
 * The Pay button carries the amount. On a busy till the cashier is looking at
 * the customer, not the screen, and a button that says "Pay Rs. 1,705.00" is
 * confirmable at a glance in a way that a button saying "Pay" is not.
 *
 * The same component is the right-hand column from `lg` up and the sheet that
 * the cart bar opens below it. `onClose` is what tells it which: a sheet has to
 * offer a way out, a column must not, because closing a column that is part of
 * the layout would leave a hole. Nothing else about it changes - the sheet is
 * the real order panel, not a summary of one.
 */

import { useDispatch, useSelector } from 'react-redux';
import {
    FiPercent, FiFileText, FiPause, FiTrash2, FiPlus, FiMinus,
    FiX, FiArrowRight, FiMoreVertical, FiCreditCard, FiSmartphone, FiDollarSign
} from 'react-icons/fi';

import {
    selectCart, selectTotals, incrementLine, decrementLine, removeLine, clearCart
} from '../../store/cartSlice';
import { openModal } from '../../store/uiSlice';
import { selectPaymentModes } from '../../store/menuSlice';
import { money } from '../../utils/format';

/* ------------------------------- one bill line ---------------------------- */

const OrderLine = ({ line, onIncrement, onDecrement, onRemove, onOpenLine }) => (
    <div className="group relative border-b border-slate-100/70 px-4 py-2 last:border-b-0 hover:bg-slate-50/80 transition-colors">
        <div className="flex items-center gap-3">
            <button
                type="button"
                onClick={() => onOpenLine(line)}
                className="min-w-0 flex-1 text-left outline-none"
            >
                <p className="truncate text-[13px] font-bold leading-tight text-slate-800">
                    {line.itemName}
                    {line.variantName && (
                        <span className="font-medium text-slate-400"> · {line.variantName}</span>
                    )}
                </p>

                {line.modifiers?.length > 0 && (
                    <p className="truncate text-[11px] font-medium text-slate-400 mt-0.5">
                        {line.modifiers.map((m) => m.name).join(', ')}
                    </p>
                )}

                {line.itemNote && (
                    <p className="truncate text-[11px] font-medium italic text-amber-600 mt-0.5">
                        * {line.itemNote}
                    </p>
                )}

                {line.discountAmount > 0 && (
                    <p className="text-[11px] font-semibold text-emerald-600 mt-0.5">
                        Discount {line.discountPercent > 0 ? `${line.discountPercent}%` : ''} −{money(line.discountAmount)}
                    </p>
                )}

                {/* A line already in the kitchen cannot simply be deleted - the
                    food is being cooked. Saying so is better than a control that
                    silently does nothing. */}
                {line.isSentToKitchen && (
                    <p className="text-[11px] font-bold uppercase tracking-wider text-blue-500 mt-1">
                        In Kitchen
                    </p>
                )}
            </button>

            <div className="flex shrink-0 items-center justify-between rounded-lg border border-slate-200/80 bg-white p-0.5 shadow-sm">
                <button
                    type="button"
                    onClick={() => onDecrement(line.key)}
                    className="flex h-6 w-6 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                    aria-label="One fewer"
                >
                    <FiMinus className="h-3.5 w-3.5" />
                </button>

                <span className="tabular w-6 text-center text-sm font-bold text-slate-800">
                    {line.quantity}
                </span>

                <button
                    type="button"
                    onClick={() => onIncrement(line.key)}
                    className="flex h-6 w-6 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                    aria-label="One more"
                >
                    <FiPlus className="h-3.5 w-3.5" />
                </button>
            </div>

            <div className="w-20 shrink-0 text-right pr-6">
                <p className="tabular text-[13px] font-black leading-tight text-slate-800">{money(line.lineTotal)}</p>
                <p className="tabular text-[10px] font-bold leading-tight text-slate-400">@ {money(line.unitRate)}</p>
            </div>

            <button
                type="button"
                onClick={() => onRemove(line.key)}
                className="absolute right-3 top-2 flex h-6 w-6 items-center justify-center rounded-lg text-slate-300 opacity-0 transition-all hover:bg-red-50 hover:text-red-500 group-hover:opacity-100"
                aria-label="Remove"
            >
                <FiX className="h-4 w-4" />
            </button>
        </div>
    </div>
);

/* -------------------------------- the panel ------------------------------- */

const PAYMENT_ICONS = { CASH: FiDollarSign, CARD: FiCreditCard, WALLET: FiSmartphone };
const PAYMENT_COLOURS = {
    CASH: 'bg-emerald-700 hover:bg-emerald-600 text-white',
    CARD: 'bg-blue-500 hover:bg-blue-600 text-white',
    WALLET: 'bg-indigo-500 hover:bg-indigo-600 text-white',
    CREDIT: 'bg-slate-600 hover:bg-slate-700 text-white'
};

const OrderPanel = ({ onPay, onSendToKitchen, saving, onClose = null }) => {
    const dispatch = useDispatch();
    const cart = useSelector(selectCart);
    const totals = useSelector(selectTotals);
    const paymentModes = useSelector(selectPaymentModes);

    const isEmpty = cart.lines.length === 0;
    const hasUnsentLines = cart.lines.some((line) => !line.isSentToKitchen);

    return (
        <aside className="flex h-full w-full flex-col overflow-hidden rounded-none border-slate-200/80
                          bg-white shadow-xl shadow-slate-200/40 sm:rounded-2xl sm:border">
            {/* ── Premium Header ────────────────────────────────────────── */}
            <header
                className="shrink-0 flex items-center justify-between gap-3 px-5 py-2 [@media(max-height:820px)]:py-1.5"
                style={{ background: 'linear-gradient(135deg, #05650fff 0%, #158f21ff 100%)' }}
            >
                <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-white">
                        Order No. : {cart.orderNumber || 'ORD-XXXXXX'}
                    </p>
                    <p className="mt-0.5 truncate text-[11px] font-medium uppercase tracking-wider text-white/70">
                        {cart.orderTypeCode === 'DINE_IN'
                            ? cart.tableNumber ? `Table ${cart.tableNumber}` : 'No table chosen'
                            : cart.orderTypeCode === 'TAKE_AWAY' ? 'Take Away' : 'Home Delivery'}
                        {' · '}{cart.customerName}
                    </p>
                </div>

                <div className="flex shrink-0 items-center gap-1">
                    <button
                        type="button"
                        onClick={() => dispatch(openModal({ modal: 'orderOptions' }))}
                        className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 text-white
                                   transition-colors hover:bg-white/20 sm:h-8 sm:w-8"
                        aria-label="Order options"
                    >
                        <FiMoreVertical className="h-4 w-4" />
                    </button>

                    {onClose && (
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 text-white
                                       transition-colors hover:bg-white/20 sm:h-8 sm:w-8"
                            aria-label="Back to the items"
                        >
                            <FiX className="h-4 w-4" />
                        </button>
                    )}
                </div>
            </header>

            {/* ── Column headings ─────────────────────────────────────── */}
            <div className="shrink-0 flex items-center gap-3 border-b border-slate-100 bg-slate-50/80 px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                <span className="min-w-0 flex-1">Item</span>
                <span className="w-24 text-center">Qty</span>
                <span className="w-16 text-right pr-1">Total</span>
                <span className="w-5" />
            </div>

            {/* ── Lines ───────────────────────────────────────────────── */}
            <div className="min-h-0 flex-1 overflow-y-auto scrollbar-none">
                {isEmpty ? (
                    <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-50">
                            <FiPlus className="h-6 w-6 text-slate-300" />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-slate-500">Cart is empty</p>
                            <p className="mt-1 text-xs font-medium text-slate-400">
                                Tap an item on the left, or scan a barcode to add.
                            </p>
                        </div>
                    </div>
                ) : (
                    cart.lines.map((line) => (
                        <OrderLine
                            key={line.key}
                            line={line}
                            onIncrement={(key) => dispatch(incrementLine(key))}
                            onDecrement={(key) => dispatch(decrementLine(key))}
                            onRemove={(key) => dispatch(removeLine(key))}
                            onOpenLine={(l) => dispatch(openModal({ modal: 'lineOptions', payload: l }))}
                        />
                    ))
                )}
            </div>

            {/* ── Totals ──────────────────────────────────────────────── */}
            <div className="shrink-0 border-t border-slate-100 bg-slate-50/50 px-5 pt-2 pb-2 [@media(max-height:820px)]:pt-1.5 [@media(max-height:820px)]:pb-1.5">
                <dl className="space-y-1 text-sm">
                    <div className="flex justify-between items-center">
                        <dt className="text-xs font-semibold text-slate-500">Sub Total</dt>
                        <dd className="tabular text-sm font-bold text-slate-800">{money(totals.subTotal)}</dd>
                    </div>

                    {(totals.itemDiscount + totals.orderDiscount) > 0 && (
                        <div className="flex justify-between items-center">
                            <dt className="text-xs font-semibold text-slate-500">Discount</dt>
                            <dd className="tabular text-sm font-bold text-emerald-600">
                                −{money(totals.itemDiscount + totals.orderDiscount)}
                            </dd>
                        </div>
                    )}

                    {totals.serviceCharge > 0 && (
                        <div className="flex justify-between items-center">
                            <dt className="text-xs font-semibold text-slate-500">Service Charge</dt>
                            <dd className="tabular text-sm font-bold text-slate-800">{money(totals.serviceCharge)}</dd>
                        </div>
                    )}

                    {totals.deliveryCharge > 0 && (
                        <div className="flex justify-between items-center">
                            <dt className="text-xs font-semibold text-slate-500">Delivery</dt>
                            <dd className="tabular text-sm font-bold text-slate-800">{money(totals.deliveryCharge)}</dd>
                        </div>
                    )}

                    <div className="flex justify-between items-center">
                        <dt className="text-xs font-semibold text-slate-500">Sale Tax</dt>
                        <dd className="tabular text-sm font-bold text-slate-800">{money(totals.taxAmount)}</dd>
                    </div>

                    {totals.posServiceFee > 0 && (
                        <div className="flex justify-between items-center">
                            <dt className="text-xs font-semibold text-slate-500">POS Fee</dt>
                            <dd className="tabular text-sm font-bold text-slate-800">{money(totals.posServiceFee)}</dd>
                        </div>
                    )}

                    {totals.roundOff !== 0 && (
                        <div className="flex justify-between items-center">
                            <dt className="text-xs font-semibold text-slate-500">Round Off</dt>
                            <dd className="tabular text-sm font-bold text-slate-800">{money(totals.roundOff)}</dd>
                        </div>
                    )}

                    <div className="mt-1.5 flex items-baseline justify-between border-t border-slate-200 pt-2">
                        <dt className="font-black text-slate-800">Payable</dt>
                        <dd className="tabular text-xl [@media(max-height:820px)]:text-lg font-black text-slate-900 drop-shadow-sm">
                            {money(totals.totalPayable)}
                        </dd>
                    </div>
                </dl>
            </div>

            {/* ── Line actions ────────────────────────────────────────── */}
            <div className="shrink-0 grid grid-cols-4 gap-2 border-t border-slate-100 px-4 py-2 [@media(max-height:820px)]:py-1 bg-white">
                <button
                    type="button"
                    disabled={isEmpty}
                    onClick={() => dispatch(openModal({ modal: 'discount' }))}
                    className="flex flex-col items-center justify-center gap-1 rounded-xl bg-slate-50 py-1.5 [@media(max-height:820px)]:py-1
                               text-slate-500 transition-colors hover:bg-brand-50 hover:text-brand-700
                               disabled:opacity-50 disabled:hover:bg-slate-50 disabled:hover:text-slate-500"
                >
                    <FiPercent className="h-4 w-4" />
                    <span className="text-[10px] font-bold uppercase tracking-wider">Discount</span>
                </button>

                <button
                    type="button"
                    onClick={() => dispatch(openModal({ modal: 'note' }))}
                    className="flex flex-col items-center justify-center gap-1 rounded-xl bg-slate-50 py-1.5 [@media(max-height:820px)]:py-1
                               text-slate-500 transition-colors hover:bg-brand-50 hover:text-brand-700"
                >
                    <FiFileText className="h-4 w-4" />
                    <span className="text-[10px] font-bold uppercase tracking-wider">Note</span>
                </button>

                <button
                    type="button"
                    disabled={isEmpty}
                    onClick={() => dispatch(openModal({ modal: 'hold' }))}
                    className="flex flex-col items-center justify-center gap-1 rounded-xl bg-slate-50 py-1.5 [@media(max-height:820px)]:py-1
                               text-slate-500 transition-colors hover:bg-brand-50 hover:text-brand-700
                               disabled:opacity-50 disabled:hover:bg-slate-50 disabled:hover:text-slate-500"
                >
                    <FiPause className="h-4 w-4" />
                    <span className="text-[10px] font-bold uppercase tracking-wider">Hold</span>
                </button>

                <button
                    type="button"
                    disabled={isEmpty}
                    onClick={() => {
                        if (window.confirm('Clear this order? Nothing will be saved.')) {
                            dispatch(clearCart());
                        }
                    }}
                    className="flex flex-col items-center justify-center gap-1 rounded-xl bg-slate-50 py-1.5 [@media(max-height:820px)]:py-1
                               text-red-500 transition-colors hover:bg-red-50 hover:text-red-700
                               disabled:opacity-50 disabled:hover:bg-slate-50 disabled:hover:text-red-500"
                >
                    <FiTrash2 className="h-4 w-4" />
                    <span className="text-[10px] font-bold uppercase tracking-wider">Clear</span>
                </button>
            </div>

            {/* ── Kitchen & Payment ───────────────────────────────────── */}
            <div className="shrink-0 px-4 pb-3 [@media(max-height:820px)]:pb-2 bg-white space-y-1.5">
                {hasUnsentLines && !isEmpty && (
                    <button
                        type="button"
                        onClick={onSendToKitchen}
                        disabled={saving}
                        className="flex w-full items-center justify-center rounded-xl bg-amber-100 py-2
                                   text-sm font-bold text-amber-800 transition-colors hover:bg-amber-200"
                    >
                        Send to kitchen
                    </button>
                )}

                <div className="grid grid-cols-3 gap-2">
                    {paymentModes
                        .filter((mode) => mode.Payment_Mode_Code !== 'CREDIT')
                        .slice(0, 3)
                        .map((mode) => {
                            const Icon = PAYMENT_ICONS[mode.Payment_Mode_Code] || FiCreditCard;
                            const colorClass = PAYMENT_COLOURS[mode.Payment_Mode_Code] || 'bg-slate-100 text-slate-700 hover:bg-slate-200';

                            return (
                                <button
                                    key={mode.Payment_Mode_ID}
                                    type="button"
                                    disabled={isEmpty || saving}
                                    onClick={() => onPay(mode)}
                                    className={`flex items-center justify-center gap-1.5 rounded-xl py-1.5
                                                text-[11px] font-bold uppercase tracking-wider transition-all
                                                disabled:opacity-50 ${colorClass}`}
                                >
                                    <Icon className="h-3.5 w-3.5" />
                                    <span className="truncate">{mode.Payment_Mode_Name}</span>
                                </button>
                            );
                        })}
                </div>

                <button
                    type="button"
                    disabled={isEmpty || saving}
                    onClick={() => onPay(null)}
                    className="group relative flex w-full items-center justify-center overflow-hidden rounded-xl py-2.5 [@media(max-height:820px)]:py-2
                               text-base font-black text-white shadow-lg shadow-emerald-500/20 transition-all
                               hover:-translate-y-0.5 hover:shadow-xl hover:shadow-emerald-500/30
                               active:translate-y-0 active:shadow-md
                               disabled:pointer-events-none disabled:opacity-50"
                    style={{ background: 'linear-gradient(135deg, #05650fff 0%, #158f21ff 100%)' }}
                >
                    <div className="absolute inset-0 bg-white/20 opacity-0 transition-opacity group-hover:opacity-100" />
                    {saving ? 'Working…' : (
                        <span className="flex items-center gap-2 drop-shadow-sm">
                            Pay <span className="font-black tracking-tight text-white/90 mr-1">Rs.</span> {money(totals.totalPayable)}
                            <FiArrowRight className="h-5 w-5 ml-1 transition-transform group-hover:translate-x-1" />
                        </span>
                    )}
                </button>
            </div>
        </aside>
    );
};

export { OrderLine };
export default OrderPanel;
